import React, { useEffect, useState } from "react";
import Separator from "./Separator";
import RadioInput from "../inputs/RadioInput";
import cn from "classnames";

const DonationFrequencyAndAmount = ({
	register,
	watch,
	setValue,
	field,
	placeholder,
	error,
	required = false,
	currency = "GBP",
	id,
	config,
	frequencyStyle = "pills",
}) => {
	const frequencyValue = watch(`${id}[frequency]`);
	const amountValue = watch(`${id}[amount]`);
	const [amount, setAmount] = useState("");
	const [customAmount, setCustomAmount] = useState("");

	// Get nested errors correctly
	const frequencyError = error?.[id]?.frequency || error?.frequency;
	const amountError = error?.[id]?.amount || error?.amount;

	// Select first frequency and amount by default on mount
	useEffect(() => {
		if (!frequencyValue && field.frequencies.length > 0) {
			const firstFrequency = field.frequencies[0];
			setValue(`${id}[frequency]`, firstFrequency.frequency_slug, {
				shouldValidate: true,
			});

			// Also set the first amount of the first frequency
			if (!field.auto_select_first_amount) return;
			if (
				firstFrequency.donation_amounts &&
				firstFrequency.donation_amounts.length > 0
			) {
				const firstAmount = firstFrequency.donation_amounts[0].amount;
				setValue(`${id}[amount]`, firstAmount, {
					shouldValidate: true,
				});
			}
		}
	}, []);

	// Watch for frequency changes and update amount to first available amount
	useEffect(() => {
		if (frequencyValue) {
			const selectedFrequency = field.frequencies.find(
				(f) => f.frequency_slug === frequencyValue
			);

			// if custom amount is set, set the amount to the custom amount on change and return
			if (customAmount) {
				setValue(`${id}[amount]`, customAmount, {
					shouldValidate: true,
				});
				return;
			}

			// If frequency changed and has amounts, set to first amount
			if (field.auto_select_first_amount == false) {
				setAmount(null);
				setCustomAmount("");
				setValue(`${id}[amount]`, null, {
					shouldValidate: true,
				});
				return;
			}

			if (selectedFrequency?.donation_amounts?.length > 0) {
				const firstAmount = selectedFrequency.donation_amounts[0].amount;
				// Only set if current amount is not available in new frequency
				const currentAmountExists = selectedFrequency.donation_amounts.some(
					(amt) => amt.amount == amountValue
				);

				if (!currentAmountExists) {
					setValue(`${id}[amount]`, firstAmount, {
						shouldValidate: true,
					});
				}
			}
		}
	}, [frequencyValue]);

	//when amount changes, check if its in selectedFrequency.donation_amounts, if its not, set the custom amount to the amount
	useEffect(() => {
		if (amountValue) {
			const currentAmountOptions = field.frequencies.find(
				(f) => f.frequency_slug === frequencyValue
			)?.donation_amounts;
			if (!currentAmountOptions.some((a) => a.amount == amountValue)) {
				setAmount(null);
				setCustomAmount(amountValue);
				setValue(`${id}[amount]`, amountValue, {
					shouldValidate: true,
				});
			}
		}
	}, [amountValue]);

	const selectedFrequency = field.frequencies.find(
		(f) => f.frequency_slug === frequencyValue
	);

	// Get amounts for the selected frequency
	const availableAmounts = selectedFrequency?.donation_amounts || [];

	// Find the selected amount option to get its image and text
	const selectedAmountOption = availableAmounts.find(
		(option) => option.amount == amountValue
	);

	const getCurrencySymbol = (currency) => {
		switch (currency) {
			case "GBP":
				return "£";
			case "USD":
				return "$";
			case "EUR":
				return "€";
			default:
				return "£";
		}
	};

	// Generate unique IDs for this form instance to prevent conflicts
	const uniqueId = `${id}-${Math.random().toString(36).substr(2, 9)}`;

	return (
		<>
			<div className={cn(field.frequencies.length <= 1 ? "d-none" : "")}>
				<div className="container field-section-wrapper">
					{field.title && !field.hide_title && (
						<label className="donate-field__frequency-title large-label">
							{field.title}
						</label>
					)}
					<div className="donate-field__frequency-options">
						<RadioInput
							options={field.frequencies.map((freq) => ({
								label: freq.frequency,
								value: freq.frequency_slug,
							}))}
							label={field.amount_title}
							hideLabel={field.hide_label}
							required={required}
							register={() =>
								register(`${id}[frequency]`, {
									required: required ? "Please select a frequency" : false,
								})
							}
							id={`${uniqueId}-frequency`}
							style={frequencyStyle}
						/>
					</div>

					{/* Display frequency error */}
					{frequencyError && (
						<div className="donate-field__error">{frequencyError.message}</div>
					)}
				</div>
				{!config.hide_separators && <Separator separator_type="wide" />}
			</div>

			<div className="container field-section-wrapper">
				{field.amount_title && !field.hide_amount_title && (
					<label className="donate-field__amount-title large-label">
						{field.amount_title}
					</label>
				)}
				{selectedFrequency && (
					<div className="donate-field__amount-buttons">
						{availableAmounts.map((amountOption) => (
							<div key={amountOption.amount}>
								<input
									type="radio"
									id={`${uniqueId}-${amountOption.amount}`}
									className={`donate-field__amount-button ${
										amountValue == amountOption.amount ? "selected" : ""
									}`}
									name="amount"
									checked={amountValue == amountOption.amount}
									value={amountOption.amount}
									onChange={() => {
										setAmount(amountOption.amount);
										setCustomAmount("");
										setValue(`${id}[amount]`, amountOption.amount, {
											shouldValidate: true,
										});
									}}
								/>
								<label
									htmlFor={`${uniqueId}-${amountOption.amount}`}
									className="donate-field__amount-label"
								>
									{getCurrencySymbol(currency) + amountOption.amount}
								</label>
							</div>
						))}
						<div className="donate-field__custom-amount">
							<input
								type="number"
								className={`donate-field__input ${
									amountError ? "donate-field__input--error" : ""
								}`}
								placeholder={placeholder}
								min={selectedFrequency.min_donation_amount}
								max={selectedFrequency.max_donation_amount}
								step="5"
								value={customAmount}
								onChange={(e) => {
									setCustomAmount(e.target.value);
									setAmount("");
									setValue(`${id}[amount]`, e.target.value, {
										shouldValidate: true,
									});
								}}
								onKeyDown={(e) => {
									if (
										e.key === "e" ||
										e.key === "E" ||
										e.key === "+" ||
										e.key === "-"
									) {
										e.preventDefault();
									}
								}}
							/>
							{/* hidden input to store the amount */}
							<input
								type="hidden"
								name={`${id}[amount]`}
								{...register(`${id}[amount]`, {
									required: required ? "Please enter an amount" : false,
									min: {
										value: selectedFrequency.min_donation_amount,
										message:
											"We appreciate all donations made to The Air Ambulance Service, however we kindly ask for a minimum donation of £" +
											Number(
												selectedFrequency.min_donation_amount
											).toLocaleString("en-GB"),
									},
									max: {
										value: selectedFrequency.max_donation_amount,
										message:
											"We appreciate all donations made to The Air Ambulance Service, however due to processing limitations we aren't able to accept a value more than £" +
											Number(
												selectedFrequency.max_donation_amount
											).toLocaleString("en-GB"),
									},
									pattern: {
										value: /^\d+(\.\d{1,2})?$/,
										message:
											"Please enter a valid amount (up to 2 decimal places)",
									},
								})}
							/>
						</div>
					</div>
				)}
			</div>

			{/* Display amount error */}
			{amountError && (
				<div className="donate-field__error">{amountError.message}</div>
			)}

			{/* Summary with image - Show when both values are selected AND we have a matching amount option */}
			{amountValue &&
				selectedFrequency &&
				selectedAmountOption &&
				field.show_amount_summaries && (
					<div className="donate-field__summary-image">
						<div className="donate-field__summary-image-text">
							<div className="donate-field__summary-image-text-content">
								<div
									dangerouslySetInnerHTML={{
										__html: selectedAmountOption.amount_text,
									}}
								/>
							</div>
						</div>
					</div>
				)}
		</>
	);
};

export default DonationFrequencyAndAmount;
